import { create } from "zustand";
import {
  getReferralStats,
  getMilestoneStatus,
  getXPBalance,
  ReferralStatsResponse,
  MilestoneStatusResponse,
  XPBalanceResponse,
} from "../api/rewards";
import { useSDKConfigStore } from "./sdkConfigStore";

export interface ApiState<T> {
  isLoading: boolean;
  error: string | null;
  data: T | null;
}

interface RewardsState {
  referralStats: ApiState<ReferralStatsResponse["data"]>;
  milestoneStatus: ApiState<MilestoneStatusResponse["data"]>;
  xpBalance: ApiState<XPBalanceResponse["data"]>;

  // Actions
  setReferralStats: (
    state: Partial<ApiState<ReferralStatsResponse["data"]>>
  ) => void;
  setMilestoneStatus: (
    state: Partial<ApiState<MilestoneStatusResponse["data"]>>
  ) => void;
  setXPBalance: (state: Partial<ApiState<XPBalanceResponse["data"]>>) => void;

  fetchReferralStats: () => Promise<void>;
  fetchMilestoneStatus: () => Promise<void>;
  fetchXPBalance: () => Promise<void>;

  reset: () => void;
}

const initialApiState = {
  isLoading: false,
  error: null,
  data: null,
};

export const useRewardsStore = create<RewardsState>((set, get) => ({
  // Initial state
  referralStats: { ...initialApiState },
  milestoneStatus: { ...initialApiState },
  xpBalance: { ...initialApiState },

  // Setters
  setReferralStats: (state) =>
    set((prev) => ({
      referralStats: { ...prev.referralStats, ...state },
    })),

  setMilestoneStatus: (state) =>
    set((prev) => ({
      milestoneStatus: { ...prev.milestoneStatus, ...state },
    })),

  setXPBalance: (state) =>
    set((prev) => ({
      xpBalance: { ...prev.xpBalance, ...state },
    })),

  // Fetch methods
  fetchReferralStats: async () => {
    const { isInitialized } = useSDKConfigStore.getState();

    if (!isInitialized) {
      console.error("SDK: Cannot fetch referral stats - SDK not initialized");
      return;
    }

    get().setReferralStats({ isLoading: true, error: null });

    try {
      const response = await getReferralStats();
      get().setReferralStats({ data: response.data, isLoading: false });
      console.log("SDK: Successfully fetched referral stats:", response.data);
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to fetch referral stats";
      console.error("SDK: Error fetching referral stats:", errorMessage);
      get().setReferralStats({ error: errorMessage, isLoading: false });
    }
  },

  fetchMilestoneStatus: async () => {
    const { isInitialized } = useSDKConfigStore.getState();

    if (!isInitialized) {
      console.error("SDK: Cannot fetch milestone status - SDK not initialized");
      return;
    }

    get().setMilestoneStatus({ isLoading: true, error: null });

    try {
      const response = await getMilestoneStatus();
      get().setMilestoneStatus({ data: response.data, isLoading: false });
      console.log(
        "SDK: Successfully fetched milestone status:",
        response.data.milestones.length
      );
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to fetch milestone status";
      console.error("SDK: Error fetching milestone status:", errorMessage);
      get().setMilestoneStatus({ error: errorMessage, isLoading: false });
    }
  },

  fetchXPBalance: async () => {
    const { isInitialized } = useSDKConfigStore.getState();

    if (!isInitialized) {
      console.error("SDK: Cannot fetch XP balance - SDK not initialized");
      return;
    }

    get().setXPBalance({ isLoading: true, error: null });

    try {
      const response = await getXPBalance();
      get().setXPBalance({ data: response.data, isLoading: false });
      console.log(
        "SDK: Successfully fetched XP balance:",
        response.data.xpBalance
      );
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to fetch XP balance";
      console.error("SDK: Error fetching XP balance:", errorMessage);
      get().setXPBalance({ error: errorMessage, isLoading: false });
    }
  },

  reset: () =>
    set({
      referralStats: { ...initialApiState },
      milestoneStatus: { ...initialApiState },
      xpBalance: { ...initialApiState },
    }),
}));
